import { createFileRoute, Link } from '@tanstack/react-router'
import { BRAND_FALLBACK } from '@/components/marketing/config'
import { Brand, LegalPage, SupportEmail } from '@/components/marketing/LegalPage'

export const Route = createFileRoute('/subprocessors')({
  head: () => ({
    meta: [
      { title: `Subprocessors · ${BRAND_FALLBACK}` },
      { name: 'description', content: 'The companies that process data for us, what each one handles, and where.' },
    ],
  }),
  component: Subprocessors,
})

const ROWS: Array<{ name: string; purpose: string; data: string; location: string }> = [
  {
    name: 'Cloudflare',
    purpose:
      'Hosts this website, previews and customer sites. Provides databases, file storage, SSL certificates for connected domains, and bot checks (Turnstile).',
    data: 'Site content, account and order data, contact form submissions, IP addresses',
    location: 'United States, with a global network',
  },
  {
    name: 'Stripe',
    purpose: 'Payments, invoices and the billing portal.',
    data: 'Name, email, billing address, tax ID, payment details',
    location: 'United States and Ireland',
  },
  {
    name: 'Anthropic',
    purpose: 'Claude models that rebuild pages, fix issues and review every site.',
    data: 'Public site content only',
    location: 'United States',
  },
  {
    name: 'OpenRouter',
    purpose: 'Routes requests to DeepSeek models.',
    data: 'Public site content only. Not used with Claude-only processing.',
    location: 'United States',
  },
  {
    name: 'DeepSeek, through OpenRouter',
    purpose: 'Models that build and fix pages at lower cost.',
    data: 'Public site content only. Not used with Claude-only processing.',
    location: 'China',
  },
  {
    name: 'Hetzner',
    purpose: 'Servers that run the rebuild, and weekly backup copies of site databases (kept 8 weeks).',
    data: 'Site content, backups',
    location: 'Germany',
  },
  {
    name: 'Cloudflare Email Service',
    purpose: 'Sends emails about your order: audit and preview links, receipts, reminders and support replies.',
    data: 'Email address, email content',
    location: 'Global network',
  },
]

function Subprocessors() {
  return (
    <LegalPage
      title="Subprocessors"
      wide
      intro={
        <p>
          These are the companies that process data on behalf of <Brand />. None of the AI model providers receive
          personal data: they only see the public content of the site being rebuilt.
        </p>
      }
    >
      <div className="-mx-4 overflow-x-auto px-4">
        <table className="min-w-[40rem]">
          <caption className="sr-only">Subprocessors, what they do, the data they handle and where</caption>
          <thead>
            <tr>
              <th scope="col">Company</th>
              <th scope="col">What they do for us</th>
              <th scope="col">Data they handle</th>
              <th scope="col">Location</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.name}>
                <th scope="row" className="font-semibold">
                  {r.name}
                </th>
                <td>{r.purpose}</td>
                <td>{r.data}</td>
                <td>{r.location}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        We update this page when the list changes. How we use each kind of data is described in our{' '}
        <Link to="/privacy">privacy policy</Link>. Questions: <SupportEmail />.
      </p>
    </LegalPage>
  )
}
